import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/css";
import ModalLayout from "../../components/structure/ModalLayout";
import ModalHeader from "../../components/general/ModalHeader";
import FooterBar from "../../components/structure/FooterBar";
import Button from "../../components/general/Button";
import CustomScrollbars from "../../components/structure/CustomScrollbars";
import ScheduledAnalyticsEmailContainer from "../../hooks/ScheduledAnalyticsEmailContainer";
import ApplicationContainer from "../../state/containers/ApplicationContainer";
import { METRICS_TO_EXCLUDE } from "../../constants/analyticsConstants/metricsToExclude";
import { style_border_default } from "../../constants/stylesValues";
import { color_shades_darkest, color_shades_light } from "../../constants/colors";
import SmallModalLayout from "../structure/SmallModalLayout";

interface Props {
    isVisible: boolean;

    // org/app context for preview
    orgId: string;
    orgName: string;
    appId?: string;
    featureFlag: string;
    appContainer: ApplicationContainer;

    // seed + callbacks
    initialSelected?: string[];
    onSave: (excluded: string[]) => void;
    onClose: () => void;

    title?: string;                     // modal header title
    leftTitle?: string;                 // left pane title
    rightTitle?: string;                // right pane title

    isSparkView?: boolean;
};

const SplitAnalyticsEmailModal: React.FC<Props> = ({
    isVisible,
    orgId,
    orgName,
    appId,
    featureFlag,
    appContainer,
    initialSelected: initialExcluded,
    onSave,
    onClose,
    title = "Email Preview",
    leftTitle = "Metrics to Include",
    rightTitle = "Email Preview",
    isSparkView = false
}) => {

    const scheduled = ScheduledAnalyticsEmailContainer.useContainer();
    const iframeRef = useRef<HTMLIFrameElement | null>(null);

    const [appName, setAppName] = useState<string | null>(null);
    const [html, setHtml] = useState<string>("");
    const [fitWidth, setFitWidth] = useState<boolean>(true);
    const [iframeHeight, setIframeHeight] = useState<number>(640);

    const StyledButton = styled(Button)`
        position: relative;
        left: -15px;
        height: 30px;
        width: 90px;
        font-size: 13px;
    `;

    const PanesWrapper = styled.div`
        display: flex;
        align-items: stretch;
        gap: 0;
        padding: 0 18px 0 18px;
        min-height: 560px;
    `;

    const LeftPane = styled.section`
        display: flex;
        flex-direction: column;
        min-width: 260px;
        border-right: ${style_border_default};
        padding: 12px 16px 0 16px;
        flex: 1 1 0;
    `;

    const RightPane = styled.section`
        display: flex;
        flex-direction: column;
        min-width: 320px;
        padding: 12px 16px 0 16px;
        flex: 1 1 0;
    `;
    const PaneHeader = styled.h3`
        margin: 0 0 0 0;
        font-size: 14px;
        font-weight: 600;
        padding: 4px 18px 0 18px;
    `;

    const ControlsRow = styled.div`
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 4px 0px 4px 16px;
    `;

    const muted = css`
        font-size: 13px;
        color: ${color_shades_darkest};
        opacity: 0.85;
    `;

    const PaneBody = styled.div`
        border-top: ${style_border_default};
    `;

    const Grid = styled.div`
        display: grid;
        grid-template-columns: 1fr 1fr; /* 2 columns */
        gap: 8px 16px;
        padding-right: 6px;
        @media (max-width: 900px) {
            grid-template-columns: 1fr; /* stack on narrow viewports */
        }
    `;

    const MetricCell = styled.div`
        padding: 8px 0;
    `;

    const CheckboxLabel = styled.label`
        display: inline-flex;
        align-items: center;
        gap: 10px;
        font-size: 13px;

        input {
            width: 16px;
            height: 16px;
            margin: 0;
        }
    `;

    const PreviewShell = styled.div<{ fitWidth: boolean }>`
        ${p => (!p.fitWidth ? `overflow-x: auto;` : ``)}
    `;

    const FrameWrap = styled.div<{ fitWidth: boolean }>`
        display: flex;
        justify-content: ${p => (p.fitWidth ? "stretch" : "flex-start")};
        align-items: flex-start;
        ${p =>
            !p.fitWidth
                ? `
            background: repeating-linear-gradient(
            45deg,
            #fafafa,
            #fafafa 10px,
            #f6f6f6 10px,
            #f6f6f6 20px
            );
            padding: 12px;
            border-radius: 8px;
        `
                : ``}
        `;
    // ----- LEFT PANE: Metrics selection (include toggles) -----
    let metrics = METRICS_TO_EXCLUDE(featureFlag); // [key,label][]
    const SPARK_VIEW_EXCLUDED_METRICS = new Set();

    //Todo: if isSparkView, filter out metrics we don't want to show clients
    if (isSparkView) {
        metrics = metrics.filter(([key]) => !SPARK_VIEW_EXCLUDED_METRICS.has(key));
    }

    // Convert "excluded" seed into an "included set" for checkbox UX
    const initialIncluded = useMemo(() => {
        const excludedSet = new Set(initialExcluded);
        return metrics
            .filter(([key]) => !excludedSet.has(key))
            .map(([key]) => key);
    }, [featureFlag, JSON.stringify(initialExcluded)]);

    const [includedSet, setIncludedSet] = useState<Set<string>>(new Set(initialIncluded));

    useEffect(() => {
        // keep in sync when re-opening or props change
        if (isVisible) setIncludedSet(new Set(initialIncluded));
    }, [isVisible, initialIncluded.join("|")]);

    const toggle = (key: string) => {
        setIncludedSet(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };
    const selectAll = () => {
        if (includedSet.size === metrics.length) {
            setIncludedSet(new Set())
        } else {
            setIncludedSet(new Set(metrics.map(m => m[0])));
        }
    }

    // derived excluded list (for saving and for preview input)
    const excludedList = useMemo(
        () => metrics.filter(([key]) => !includedSet.has(key)).map(([key]) => key),
        [includedSet, featureFlag]
    );

    const anyChanges = useMemo(() => {
        const a = [...excludedList].sort().join("|");
        const b = [...(initialExcluded || [])].sort().join("|");
        return a !== b;
    }, [excludedList, initialExcluded]);


    // resolve app name (once per open)
    useEffect(() => {
        if (!isVisible) return;
        (async () => {
            try {
                const result = await appContainer.getApplicationsForOrg(orgId);
                const selectedApp = result?.data?.find((a: any) => a.id === appId);
                setAppName(selectedApp?.name ?? null);
            } catch {
                setAppName(null);
            }
        })();
    }, [isVisible, orgId, appId, appContainer]);

    // load/refresh preview whenever pane is open + dependencies change
    useEffect(() => {
        if (!isVisible) {
            setHtml("");
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await scheduled.preview(
                    orgId,
                    orgName,
                    excludedList,   // IMPORTANT: pass excluded metrics
                    featureFlag,
                    appId,
                    appName ?? undefined
                );
                if (!cancelled && res != null) setHtml(res);
            } catch (err) {
                console.error("Failed to load preview:", err);
                if (!cancelled) setHtml("<p>Failed to load preview.</p>");
            }
        })();
        return () => { cancelled = true; };
    }, [
        isVisible,
        orgId,
        orgName,
        appId,
        appName,
        featureFlag,
        excludedList.join("|"), // change preview when metrics change
        scheduled
    ]);

    const srcDoc = useMemo(() => {
        const hasHtmlTag = /<html[\s>]/i.test(html || "");
        if (hasHtmlTag) return html || "<!doctype html><html><body><p>No content.</p></body></html>";
        // wrap fragments
        return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8"/>
    <meta name="viewport" content="width=device-width, initial-scale=1"/>
    <style>
      html, body { margin:0; padding:0; background:#fff; }
      body { -webkit-font-smoothing: antialiased; text-rendering: optimizeLegibility; }
      img { max-width: 100%; height: auto; }
      table { border-collapse: collapse; }
    </style>
  </head>
  <body>${html || "<p>No content.</p>"}</body>
</html>`;
    }, [html]);

    const handleLoad = useCallback(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;
        try {
            const doc = iframe.contentDocument || iframe.contentWindow?.document;
            if (!doc) return;

            // ensure white bg for readability
            const baseStyle = doc.createElement("style");
            baseStyle.innerHTML = `html,body{margin:0} body{background:#fff}`;
            doc.head?.appendChild(baseStyle);

            const h = Math.max(
                doc.body?.scrollHeight || 0,
                doc.documentElement?.scrollHeight || 0,
                640
            );
            setIframeHeight(Math.min(h, 1800));
        } catch {
            // ignore
        }
    }, []);

    // reset zoom/height each time it opens
    useEffect(() => {
        if (isVisible) {
            setFitWidth(true);
            setIframeHeight(640);
        }
    }, [isVisible]);

    // ----- Save / Cancel -----
    const handleSave = () => onSave(excludedList);

    return (
        <SmallModalLayout isVisible={isVisible} onClose={onClose}>
            <ModalHeader title={title} onClose={onClose} />
            {/* <PanesWrapper id="split-modal-panels">
                {/* LEFT: Metrics pane */}
            {/* <LeftPane>
                    <PaneHeader>{leftTitle}</PaneHeader>
                    <ControlsRow>
                        <span className={muted}>Check the metrics you want to include.</span>
                    </ControlsRow>

                    <PaneBody>
                        <CustomScrollbars autoHide autoHeight autoHeightMax={520}>
                            <Grid>
                                {metrics.map(([key, label]) => (
                                    <MetricCell key={key}>
                                        <CheckboxLabel htmlFor={`metric-${key}`}>
                                            <input
                                                id={`metric-${key}`}
                                                type="checkbox"
                                                checked={includedSet.has(key)}
                                                onChange={() => toggle(key)}
                                            />
                                            <span>{label}</span>
                                        </CheckboxLabel>
                                    </MetricCell>
                                ))}
                            </Grid>
                        </CustomScrollbars>
                    </PaneBody>
                    <StyledButton themes={["secondary"]} type="button" text="Select All" onClick={selectAll} />
                </LeftPane> */}

            {/* RIGHT: Preview pane */}
            {/* <RightPane> */}
            {/* <PaneHeader>{rightTitle}</PaneHeader> */}
            <ControlsRow>
                <span className={muted}>
                    Data is randomly generated for demonstration.
                </span>
            </ControlsRow>

            <PaneBody>
                <PreviewShell fitWidth={fitWidth}>
                    <CustomScrollbars autoHide autoHeight autoHeightMax={520}>
                        <FrameWrap fitWidth={fitWidth}>
                            <iframe
                                ref={iframeRef}
                                title="Email Preview"
                                sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                                srcDoc={srcDoc}
                                onLoad={handleLoad}
                                style={{
                                    width: fitWidth ? "100%" : 800,
                                    height: iframeHeight,
                                    border: "none",
                                    background: "#fff"
                                }}
                            />
                        </FrameWrap>
                    </CustomScrollbars>
                </PreviewShell>
            </PaneBody>
            {/* </RightPane>
            </PanesWrapper> */}

            {/* <FooterBar>
                <Button themes={["primary", "start"]} text="Save" onClick={handleSave} disabled={!anyChanges} />
                <Button themes={["secondary", "end"]} text="Cancel" onClick={onClose} />
            </FooterBar> */}
        </SmallModalLayout>
    );
};

export default SplitAnalyticsEmailModal;
